import { Routes } from '@angular/router';
import { adminGuard } from './guards/admin.guard';
import { translatedTitle } from './resolver/translated-title.resolver';
import { postingGuard } from './guards/posting.guard';
import { advertisingGuard } from './guards/advertising.guard';
import { pendingChangesGuard } from './guards/pendingChanges.guard';

export const routes: Routes = [
  {
    path: 'logout',
    loadComponent: () =>
      import('./pages/logout/logout.page').then((m) => m.LogoutPageComponent),
    title: translatedTitle('LOG_OUT'),
  },
  {
    path: 'login',
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/login/login.page').then((m) => m.LoginPageComponent),
        title: translatedTitle('LOG_IN_SIGN_IN'),
      },
      {
        path: ':uid/verification',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/login/verification/verification.page').then(
                (m) => m.VerificationPageComponent,
              ),
          },
          {
            path: 'google-auth',
            loadComponent: () =>
              import('./pages/login/verification/verify-google-auth/verify-google-auth.page').then(
                (m) => m.VerifyGoogleAuthPageComponent,
              ),
          },
          {
            path: 'email',
            loadComponent: () =>
              import('./pages/login/verification/verify-email/verify-email.page').then(
                (m) => m.VerifyEmailPageComponent,
              ),
          },
          {
            path: 'phone',
            loadComponent: () =>
              import('./pages/login/verification/verify-phone/verify-phone.page').then(
                (m) => m.VerifyPhonePageComponent,
              ),
          },
        ],
      },
    ],
  },
  {
    path: 'sign-up',
    children: [
      {
        path: ':inviteCode',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/sign-up/sign-up.page').then(
                (m) => m.SignUpPageComponent,
              ),
            title: translatedTitle('LOG_IN_SIGN_UP'),
          },
          {
            path: 'password',
            loadComponent: () =>
              import('./pages/sign-up/set-up-password/set-up-password.page').then(
                (m) => m.SetUpPasswordPageComponent,
              ),
            title: translatedTitle('LOG_IN_SIGN_UP'),
          },
          {
            path: 'details',
            loadComponent: () =>
              import('./pages/sign-up/set-up-details/set-up-details.page').then(
                (m) => m.SetUpDetailsPageComponent,
              ),
            title: translatedTitle('LOG_IN_SIGN_UP'),
          },
          {
            path: '2fa',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/sign-up/set-up-2fa/set-up-2fa.page').then(
                    (m) => m.SetUp2FAPageComponent,
                  ),
                title: translatedTitle('LOG_IN_SIGN_UP'),
              },
              {
                path: 'email',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/sign-up/set-up-2fa/set-up-email/set-up-email.page').then(
                        (m) => m.SetUpEmailPageComponent,
                      ),
                    title: translatedTitle('LOG_IN_SIGN_UP'),
                  },
                  {
                    path: 'verify',
                    loadComponent: () =>
                      import('./pages/sign-up/set-up-2fa/verify-email/verify-email.page').then(
                        (m) => m.VerifyEmailPageComponent,
                      ),
                    title: translatedTitle('LOG_IN_SIGN_UP'),
                  },
                ],
              },
              {
                path: 'phone',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/sign-up/set-up-2fa/set-up-phone/set-up-phone.page').then(
                        (m) => m.SetUpPhonePageComponent,
                      ),
                    title: translatedTitle('LOG_IN_SIGN_UP'),
                  },
                  {
                    path: 'verify',
                    loadComponent: () =>
                      import('./pages/sign-up/set-up-2fa/verify-phone/verify-phone.page').then(
                        (m) => m.VerifyPhonePageComponent,
                      ),
                    title: translatedTitle('LOG_IN_SIGN_UP'),
                  },
                ],
              },
              {
                path: 'auth',
                loadComponent: () =>
                  import('./pages/sign-up/set-up-2fa/set-up-auth/set-up-auth.page').then(
                    (m) => m.SetUpAuthPageComponent,
                  ),
                title: translatedTitle('LOG_IN_SIGN_UP'),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./pages/login/forgot-password/forgot-password.page').then(
        (m) => m.ForgotPasswordPageComponent,
      ),
    title: translatedTitle('LOG_IN_FORGOT_PASSWORD'),
  },
  {
    path: 'reset-password/:token',
    loadComponent: () =>
      import('./pages/login/reset-password/reset-password.page').then(
        (m) => m.ResetPasswordPageComponent,
      ),
    title: translatedTitle('BUTTON_RESET_PASSWORD'),
  },
  {
    path: 'admin',
    loadComponent: () =>
      import('./pages/dashboard/dashboard.page').then(
        (m) => m.DashboardPageComponent,
      ),
    canActivate: [adminGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/home/home.page').then((m) => m.HomePageComponent),
        title: translatedTitle('HOME'),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile/profile.page').then(
            (m) => m.ProfilePageComponent,
          ),
        title: translatedTitle('ADMINS_PROFILE'),
      },
      {
        path: 'announcements',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/announcements/announcements.page').then(
                (m) => m.AnnouncementsPageComponent,
              ),
            title: translatedTitle('ANNOUNCEMENTS'),
          },
          {
            path: ':id/details',
            loadComponent: () =>
              import('./pages/announcements/edit-announcements/edit-announcements.page').then(
                (m) => m.EditAnnouncementsPageComponent,
              ),
          },
        ],
      },
      {
        path: 'locations',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/locations/locations.page').then(
                (m) => m.LocationsPageComponent,
              ),
            title: translatedTitle('LOCATIONS'),
          },
          {
            path: 'connect-locations',
            loadComponent: () =>
              import('./pages/locations/connect-locations/connect-locations.page').then(
                (m) => m.ConnectLocationsPageComponent,
              ),
            title: translatedTitle('LOCATIONS_CONNECT_LOCATIONS'),
          },
          {
            path: 'google-business-profile',
            loadComponent: () =>
              import(
                './pages/locations/google-business-profile/google-business-profile.page'
              ).then((m) => m.GoogleBusinessProfilePageComponent),
            title: translatedTitle('GBP_TITLE'),
          },
          {
            path: 'upload-locations/:refId',
            loadComponent: () =>
              import('./pages/locations/upload-locations/upload-locations.page').then(
                (m) => m.UploadLocationsPageComponent,
              ),
            title: translatedTitle('LOCATIONS_UPLOAD'),
          },
          {
            path: ':locationId/close',
            loadComponent: () =>
              import('./pages/locations/close-location/close-location.page').then(
                (m) => m.CloseLocationPageComponent,
              ),
            title: translatedTitle('LOCATIONS_PERMANENTLY_CLOSE'),
          },
          {
            path: ':locationId/details',
            loadComponent: () =>
              import('./pages/locations/location-details/location-details.page').then(
                (m) => m.LocationDetailsPageComponent,
              ),
            canDeactivate: [pendingChangesGuard],
          },
          {
            path: 'local-access-link',
            loadComponent: () =>
              import('./pages/locations/local-access-link/local-access-link.page').then(
                (m) => m.LocalAccessLinkPageComponent,
              ),
            title: translatedTitle('LOCAL_ACCESS_LINK'),
          },
          {
            path: ':locationId/google-business-profile/local-access-link',
            loadComponent: () =>
              import(
                './pages/locations/google-business-local-access-link/google-business-local-access-link.page'
              ).then(
                (m) => m.GoogleBusinessLocalAccessLinkPageComponent,
              ),
            title: translatedTitle('GBP_LOCAL_ACCESS_TITLE'),
          },
        ],
      },
      {
        path: 'google-business-profile',
        loadComponent: () =>
          import(
            './pages/google-business-profile/google-business-profiles.page'
          ).then((m) => m.GoogleBusinessProfilesPageComponent),
        children: [
          {
            path: '',
            redirectTo: 'reviews',
            pathMatch: 'full',
          },
          {
            path: 'reviews',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './pages/google-business-profile/google-business-reviews.page'
                  ).then((m) => m.GoogleBusinessReviewsPageComponent),
                title: translatedTitle('GBP_REVIEWS_TITLE'),
              },
              {
                path: 'feed',
                loadComponent: () =>
                  import(
                    './pages/google-business-profile/google-business-reviews-feed.page'
                  ).then((m) => m.GoogleBusinessReviewsFeedPageComponent),
                title: translatedTitle('GBP_REVIEWS_FEED_TITLE'),
              },
            ],
          },
          {
            path: 'posts',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './pages/google-business-profile/google-business-posts.page'
                  ).then((m) => m.GoogleBusinessPostsPageComponent),
                title: translatedTitle('GBP_POSTS_TITLE'),
              },
              {
                path: 'jobs',
                loadComponent: () =>
                  import(
                    './pages/google-business-profile/google-business-post-jobs.page'
                  ).then((m) => m.GoogleBusinessPostJobsPageComponent),
                title: translatedTitle('GBP_POST_JOBS_TITLE'),
              },
              {
                path: 'jobs/:jobId',
                loadComponent: () =>
                  import(
                    './pages/google-business-profile/google-business-post-job-detail.page'
                  ).then((m) => m.GoogleBusinessPostJobDetailPageComponent),
                title: translatedTitle('GBP_POST_JOB_DETAIL_TITLE'),
              },
            ],
          },
          {
            path: 'bulk-edits',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import(
                    './pages/google-business-profile/google-business-bulk-edits.page'
                  ).then((m) => m.GoogleBusinessBulkEditsPageComponent),
                title: translatedTitle('GBP_BULK_EDITS_TITLE'),
              },
              {
                path: 'new',
                loadComponent: () =>
                  import(
                    './pages/google-business-profile/google-business-bulk-edit-wizard.page'
                  ).then((m) => m.GoogleBusinessBulkEditWizardPageComponent),
                title: translatedTitle('GBP_BULK_NEW_TITLE'),
              },
              {
                path: 'jobs/:jobId',
                loadComponent: () =>
                  import(
                    './pages/google-business-profile/google-business-bulk-job-detail.page'
                  ).then((m) => m.GoogleBusinessBulkJobDetailPageComponent),
                title: translatedTitle('GBP_BULK_JOB_DETAIL_TITLE'),
              },
            ],
          },
        ],
      },
      {
        path: 'groups',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/groups/groups.page').then(
                (m) => m.GroupsPageComponent,
              ),
            title: translatedTitle('GROUPS'),
          },
          {
            path: ':groupId/details',
            loadComponent: () =>
              import('./pages/groups/group-details/group-details.page').then(
                (m) => m.GroupDetailsPageComponent,
              ),
          },
        ],
      },
      {
        path: 'asset-library',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/asset-library/asset-library.page').then(
                (m) => m.AssetLibraryPageComponent,
              ),
          },
          {
            path: ':assetId/details',
            loadComponent: () =>
              import('./pages/asset-library/asset-details/asset-details.page').then(
                (m) => m.AssetDetailsPageComponent,
              ),
          },
          {
            path: 'tag/:tagId/details',
            loadComponent: () =>
              import('./pages/asset-library/tag-details/tag-details.page').then(
                (m) => m.TagDetailsPageComponent,
              ),
          },
          {
            path: 'category/:categoryId/details',
            loadComponent: () =>
              import('./pages/asset-library/category-details/category-details.page').then(
                (m) => m.CategoryDetailsPageComponent,
              ),
          },
        ],
      },
      {
        path: 'leads-delivery',
        loadComponent: () =>
          import('./pages/leads-delivery/leads-delivery.page').then(
            (m) => m.LeadsDeliveryPageComponent,
          ),
        title: translatedTitle('LEADS_DELIVERY'),
        canDeactivate: [pendingChangesGuard],
      },
      {
        path: 'admins',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/admins/admins.page').then(
                (m) => m.AdminsPageComponent,
              ),
            title: translatedTitle('ADMINS'),
          },
          {
            path: 'invite',
            loadComponent: () =>
              import('./pages/admins/invite/invite.page').then(
                (m) => m.InvitePageComponent,
              ),
            title: translatedTitle('ADMINS_INVITE_ADMIN'),
          },
          {
            path: ':userId/profile',
            loadComponent: () =>
              import('./pages/admins/profile/profile.page').then(
                (m) => m.ProfilePageComponent,
              ),
          },
        ],
      },
      {
        path: 'dynamic-text',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/dynamic-text/dynamic-text.page').then(
                (m) => m.DynamicTextPageComponent,
              ),
          },
          {
            path: ':dynamicTextId',
            children: [
              {
                path: 'details',
                loadComponent: () =>
                  import('./pages/dynamic-text/dynamic-text-details/dynamic-text-details.page').then(
                    (m) => m.DynamicTextDetailsPageComponent,
                  ),
              },
              {
                path: 'locations-missing',
                loadComponent: () =>
                  import('./pages/dynamic-text/locations-missing-details/locations-missing-details.page').then(
                    (m) => m.LocationsMissingDetailsPageComponent,
                  ),
              },
            ],
          },
          {
            path: 'upload-dynamic-text/:refId',
            loadComponent: () =>
              import('./pages/dynamic-text/upload-dynamic-text/upload-dynamic-text.page').then(
                (m) => m.UploadDynamicTextPageComponent,
              ),
            title: translatedTitle('DYNAMIC_TEXT_UPLOAD'),
          },
        ],
      },
      {
        path: 'access',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/access/access.page').then(
                (m) => m.AccessPageComponent,
              ),
          },
          {
            path: ':accessId/details',
            loadComponent: () =>
              import('./pages/access/account-details/account-details.page').then(
                (m) => m.AccountDetailsPageComponent,
              ),
          },
          {
            path: 'user/:userId/details',
            loadComponent: () =>
              import('./pages/access/user-details/user-details.page').then(
                (m) => m.UserDetailsPageComponent,
              ),
          },
          {
            path: 'invite-user',
            loadComponent: () =>
              import('./pages/access/invite-user/invite-user.page').then(
                (m) => m.InviteUserPageComponent,
              ),
          },
        ],
      },
      {
        path: 'settings',
        children: [
          {
            path: 'national',
            loadComponent: () =>
              import('./pages/settings/national-settings/national-settings.page').then(
                (m) => m.NationalSettingsPageComponent,
              ),
            title: translatedTitle('SETTINGS_NATIONAL'),
          },
          {
            path: 'ad',
            loadComponent: () =>
              import('./pages/settings/ad-settings/ad-settings.page').then(
                (m) => m.AdSettingsPageComponent,
              ),
            title: translatedTitle('SETTINGS_AD'),
            canActivate: [advertisingGuard],
          },
          {
            path: 'post',
            loadComponent: () =>
              import('./pages/settings/post-settings/post-settings.page').then(
                (m) => m.PostSettingsPageComponent,
              ),
            title: translatedTitle('SETTINGS_POST'),
            canActivate: [postingGuard],
          },
        ],
      },
      // {
      //   path: 'push-notifications',
      //   loadComponent: () =>
      //     import('./pages/push-notifications/push-notifications.page').then(
      //       (m) => m.PushNotificationsPageComponent,
      //     ),
      //   title: translatedTitle('PUSH_NOTIFICATIONS'),
      // },
      {
        path: 'support',
        children: [
          {
            path: '',
            // loadComponent: () =>
            //   import('./pages/support/support.page').then(
            //     (m) => m.SupportPageComponent,
            //   ),
            pathMatch: 'full',
            redirectTo: '/admin',
            // title: translatedTitle('SUPPORT'),
          },
          {
            path: 'contact-us',
            loadComponent: () =>
              import('./pages/support/contact-us/contact-us.page').then(
                (m) => m.ContactUsPageComponent,
              ),
            title: translatedTitle('HOME_CONTACT_US'),
          },
          {
            path: 'help-center',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/support/help-center/help-center.page').then(
                    (m) => m.HelpCenterPageComponent,
                  ),
                title: translatedTitle('HOME_RESOURCES_HELP_CENTER'),
              },
              {
                path: 'billing',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./pages/support/help-center/billing-help/billing-help.page').then(
                        (m) => m.BillingHelpPageComponent,
                      ),
                    title: translatedTitle('HOME_RESOURCES_HELP_CENTER'),
                  },
                  {
                    path: 'card-expired',
                    loadComponent: () =>
                      import('./pages/support/help-center/billing-help/card-expired/card-expired.page').then(
                        (m) => m.CardExpiredPageComponent,
                      ),
                    title: translatedTitle('HOME_RESOURCES_HELP_CENTER'),
                  },
                ],
              },
            ],
          },
          {
            path: 'webinars',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/support/webinars/webinars.page').then(
                    (m) => m.WebinarsPageComponent,
                  ),
                title: translatedTitle('WEBINARS'),
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/support/webinars/webinar-details/webinar-details.page').then(
                    (m) => m.WebinarDetailsPageComponent,
                  ),
                title: translatedTitle('WEBINARS'),
              },
            ],
          },
          {
            path: 'newsletters',
            loadComponent: () =>
              import('./pages/support/newsletters/newsletters.page').then(
                (m) => m.NewslettersPageComponent,
              ),
            title: translatedTitle('NEWSLETTERS'),
          },
          {
            path: 'community',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./pages/support/community/community.page').then(
                    (m) => m.CommunityPageComponent,
                  ),
              },
              {
                path: ':id',
                loadComponent: () =>
                  import('./pages/support/community/community-post/community-post.page').then(
                    (m) => m.CommunityPostPageComponent,
                  ),
              },
            ],
          },
        ],
      },
      // {
      //   path: 'billing',
      //   loadComponent: () =>
      //     import('./pages/billing/billing.page').then(
      //       (m) => m.BillingPageComponent,
      //     ),
      //   title: translatedTitle('BILLING'),
      // },
      {
        path: 'promo',
        loadComponent: () =>
          import('./pages/home/promo/promo.page').then(
            (m) => m.PromoPageComponent,
          ),
      },
      {
        path: 'about-franify',
        loadComponent: () =>
          import('./pages/about-franify/about-franify.page').then(
            (m) => m.AboutFranifyPageComponent,
          ),
      },
      { path: '', redirectTo: '', pathMatch: 'full' },
      {
        path: '**',
        loadComponent: () =>
          import('./pages/not-found/not-found.page').then(
            (m) => m.NotFoundPageComponent,
          ),
      },
    ],
  },
  {
    path: 'local',
    children: [
      {
        path: 'page-preview',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/locations/local-access-link/page-preview/page-preview.page').then(
                (m) => m.PagePreviewPageComponent,
              ),
            title: translatedTitle('LOCAL_ACCESS_LINK_PAGE_PREVIEW'),
          },
        ],
      },
      {
        path: 'google-business-profile/page-preview',
        loadComponent: () =>
          import(
            './pages/locations/google-business-local-access-preview/google-business-local-access-preview.page'
          ).then((m) => m.GoogleBusinessLocalAccessPreviewPageComponent),
        title: translatedTitle('LOCAL_ACCESS_LINK_PAGE_PREVIEW'),
      },
      {
        path: 'google-business-profile/:accessToken',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/locations/google-business-local-access/google-business-local-access.page'
              ).then((m) => m.GoogleBusinessLocalAccessPageComponent),
            title: translatedTitle('GBP_LOCAL_PUBLIC_TITLE'),
          },
          {
            path: 'connect-location',
            loadComponent: () =>
              import(
                './pages/locations/local-access-link/connect-google-location-local-access/connect-google-location-local-access.page'
              ).then(
                (m) => m.ConnectGoogleLocationLocalAccessPageComponent,
              ),
            title: translatedTitle('GBP_TITLE'),
          },
          {
            path: 'confirmation',
            loadComponent: () =>
              import(
                './pages/locations/local-access-link/connect-google-location-local-access/google-location-confirmation.page'
              ).then(
                (m) => m.GoogleLocationConfirmationPageComponent,
              ),
            title: translatedTitle('GBP_LOCAL_CONFIRMATION_TITLE'),
          },
        ],
      },
      {
        path: ':accessToken',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/locations/local-access-link/local-access/local-access.page').then(
                (m) => m.LocalAccessPageComponent,
              ),
          },
          {
            path: 'connect-locations',
            loadComponent: () =>
              import('./pages/locations/local-access-link/connect-locations-local-access/connect-locations-local-access.page').then(
                (m) => m.ConnectLocationsLocalAccessPageComponent,
              ),
            title: translatedTitle('LOCATIONS_CONNECT_LOCATIONS'),
          },
          {
            path: 'connect-location',
            loadComponent: () =>
              import('./pages/locations/local-access-link/connect-location-local-access/connect-location-local-access.page').then(
                (m) => m.ConnectLocationLocalAccessPageComponent,
              ),
            title: translatedTitle('LOCATIONS_CONNECT_LOCATION'),
          },
        ],
      },
    ],
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
];
